import { BaseFileFiledDto } from '@diritto/common/base/base.dto';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonSize, ButtonType } from 'constants/button';
import { ModalType } from 'constants/modal';
import { LoadingIcon } from 'icons/LoadingIcon';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useUploadCoverImage } from 'state-hooks/contents/useUploadCoverImage';
import { getCoverImageBorder, getCoverImageStyle } from 'styles/common';
import { Button } from 'ui-components/common/Button';
import { FileUpload } from 'ui-components/common/FileUpload';
import { IkImage } from 'ui-components/Imagekit/IkImage';
import { CoverImageEditorModal } from 'ui-components/modals/CoverImageEditorModal';
import { ImageRatio } from '@diritto/common/base/constants';
import { ModalHandler } from 'utils/ModalHandler';
import { DesignIcon } from 'icons/DesignIcon';
import { AttachIcon } from 'icons/AttachIcon';
import { isPC } from 'constants/layout';

interface EditContentCoverProps {
  uploadDisabled?: boolean;
  imageFile?: BaseFileFiledDto;
  onChangeCoverImage: (params: BaseFileFiledDto) => void;
}

export const EditContentCover = ({ uploadDisabled, imageFile, onChangeCoverImage }: EditContentCoverProps) => {
  const [previewWidth, setPreviewWidth] = useState(0);
  const [coverImageFile, setCoverImageFile] = useState(imageFile);
  const [showLoading, setShowLoading] = useState(true);
  const [isAccepted, setIsAccepted] = useState(false);

  const { uploadImage, isUploading } = useUploadCoverImage();

  const setCoverImage = useCallback(
    (file: BaseFileFiledDto) => {
      setShowLoading(true);
      setCoverImageFile(file);
      onChangeCoverImage(file);
    },
    [onChangeCoverImage],
  );

  useEffect(() => {
    setCoverImageFile(imageFile);
  }, [imageFile]);

  const handleInputChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) {
        return;
      }

      const res = await uploadImage(e.target.files[0]);
      if (res) {
        setCoverImage(res);
      }

      e.target.value = '';
    },
    [setCoverImage, uploadImage],
  );

  const handleBeforeFileUpload = useCallback((triggerFileSelect: () => void) => {
    ModalHandler.show(ModalType.ContentCoverPolicyAlert, {
      onConfirm: () => {
        setIsAccepted(true);
        triggerFileSelect();
      },
    });
  }, []);

  const handleSetMakeCoverImage = useCallback(
    (file: BaseFileFiledDto) => {
      setCoverImage(file);
      ModalHandler.hide(ModalType.EditCover);
    },
    [setCoverImage],
  );

  return (
    <StyledContainer>
      {(isUploading || coverImageFile) && (
        <StyledCoverImagePreview ref={(e: HTMLDivElement) => setPreviewWidth(e?.clientWidth || 0)}>
          <StyledCoverImageWrapper width={previewWidth}>
            {coverImageFile && (
              <IkImage
                transform={{
                  path: coverImageFile.path,
                  isPrivate: coverImageFile.isPrivate,
                }}
                width={previewWidth}
                alt="cover"
                onLoad={() => setShowLoading(false)}
              />
            )}
            {showLoading && (
              <StyledLoadingImage>
                <LoadingIcon />
              </StyledLoadingImage>
            )}
          </StyledCoverImageWrapper>
        </StyledCoverImagePreview>
      )}
      <StyledButtonsContainer>
        <Button
          fullWidth
          label={
            <StyledButtonLabel>
              <DesignIcon />
              표지 디자인
            </StyledButtonLabel>
          }
          buttonType={ButtonType.Primary}
          size={ButtonSize.Medium}
          buttonTypography={Typography.XXSmall}
          onClick={() => ModalHandler.show(ModalType.EditCover)}
        />
        {!uploadDisabled && (
          <div className="flex flex-col w-full">
            <StyledFileUpload
              maxSize={10}
              onChange={handleInputChange}
              onBeforeUpload={isAccepted ? undefined : handleBeforeFileUpload}>
              <Button
                fullWidth
                label={
                  <StyledButtonLabel>
                    <AttachIcon color={Color.Primary700} />
                    파일 업로드
                  </StyledButtonLabel>
                }
                buttonType={ButtonType.Secondary}
                size={ButtonSize.Medium}
                buttonTypography={Typography.XXSmall}
                disabled={isUploading}
              />
            </StyledFileUpload>
            <div className="flex flex-row items-start justify-start break-all mt-2">
              <div className="flex items-center h-[21px] mr-2">
                <div className="w-[4px] h-[4px] bg-gray-700 rounded-full" />
              </div>
              <p className="text-gray-500 text-xxs font-medium">
                모든 표지의 출처·저작권 정보는 작품 소개란에 필수 표기해주세요. 위반 시 기본 표지로 변경되거나 작품이
                비공개될 수 있어요.
              </p>
            </div>
          </div>
        )}
      </StyledButtonsContainer>
      <CoverImageEditorModal onSubmit={handleSetMakeCoverImage} />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media (${isPC}) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const StyledCoverImagePreview = styled.div`
  width: 100%;
`;

const StyledCoverImageWrapper = styled.div(
  ({ width }: { width: number }) => css`
    position: relative;
    width: ${width}px;
    height: ${width * ImageRatio}px;

    ${getCoverImageBorder()};

    & img {
      ${getCoverImageStyle()}
    }
  `,
);

const StyledLoadingImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${Color.Gray100};
  margin-bottom: 20px;
  position: absolute;
  top: 0;
`;

const StyledFileUpload = styled(FileUpload)`
  width: 100%;
`;

const StyledButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: 8px;
`;

const StyledButtonLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
